import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, X, ArrowRight, Truck, ShoppingBag } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

const FREE_SHIPPING_THRESHOLD = 150;

export const CartPage = () => {
  const { items, updateQuantity, removeItem, getCartTotal } = useCart();
  const subtotal = getCartTotal();

  if (items.length === 0) {
    return (
      <Layout>
        <div className="section-spacing container-luxury" data-testid="empty-cart">
          <div className="max-w-md mx-auto text-center">
            <ShoppingBag className="h-16 w-16 text-[#EBE5DE] mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-[#2C2420] mb-4">
              Your Cart is Empty
            </h1>
            <p className="text-[#5D5550] mb-8">
              Discover our collection of premium cashmere and wool garments.
            </p>
            <Link to="/shop">
              <Button className="btn-primary" data-testid="continue-shopping-btn">
                Continue Shopping
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="section-spacing" data-testid="cart-page">
        <div className="container-luxury">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-12"
          >
            Shopping Cart ({items.length})
          </motion.h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Cart Items */}
            <div className="lg:col-span-2">
              <div className="space-y-6">
                {items.map((item, index) => (
                  <motion.div
                    key={`${item.product_id}-${item.size}-${item.color}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex gap-6 pb-6 border-b border-[#EBE5DE]"
                    data-testid={`cart-item-${index}`}
                  >
                    {/* Image */}
                    <Link to={`/product/${item.product_id}`} className="flex-shrink-0">
                      <div className="w-24 h-32 sm:w-32 sm:h-40 bg-[#F2EFE9] overflow-hidden">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Details */}
                    <div className="flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-2">
                        <Link to={`/product/${item.product_id}`}>
                          <h3 className="font-heading text-lg text-[#2C2420] hover:text-[#C6A87C] transition-colors">
                            {item.name}
                          </h3>
                        </Link>
                        <button
                          onClick={() => removeItem(index)}
                          className="p-1 text-[#8C8580] hover:text-[#2C2420] transition-colors"
                          data-testid={`remove-item-${index}`}
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>

                      <div className="text-sm text-[#8C8580] space-y-1 mb-4">
                        {item.size && <p>Size: {item.size}</p>}
                        {item.color && <p>Color: {item.color}</p>}
                      </div>

                      <div className="mt-auto flex items-center justify-between">
                        {/* Quantity */}
                        <div className="flex items-center border border-[#EBE5DE]">
                          <button
                            onClick={() => updateQuantity(index, item.quantity - 1)}
                            className="p-2 hover:bg-[#F2EFE9] transition-colors"
                            data-testid={`decrease-qty-${index}`}
                          >
                            <Minus className="h-3 w-3 text-[#5D5550]" />
                          </button>
                          <span className="w-8 text-center text-sm text-[#2C2420]">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(index, item.quantity + 1)}
                            className="p-2 hover:bg-[#F2EFE9] transition-colors"
                            data-testid={`increase-qty-${index}`}
                          >
                            <Plus className="h-3 w-3 text-[#5D5550]" />
                          </button>
                        </div>

                        {/* Price */}
                        <p className="font-body text-lg text-[#2C2420]">
                          €{(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Continue Shopping */}
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 text-sm text-[#8C8580] hover:text-[#2C2420] transition-colors mt-8"
              >
                <ArrowRight className="h-4 w-4 rotate-180" />
                Continue Shopping
              </Link>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-[#F2EFE9] p-6 md:p-8 sticky top-32"
                data-testid="order-summary"
              >
                <h2 className="font-heading text-xl text-[#2C2420] mb-6">
                  Order Summary
                </h2>

                <div className="space-y-4 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5D5550]">Subtotal</span>
                    <span className="text-[#2C2420]" data-testid="subtotal">
                      €{subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#5D5550]">Shipping</span>
                    <span className="text-[#2C2420]" data-testid="shipping-cost">
                      {subtotal >= FREE_SHIPPING_THRESHOLD ? 'Free' : 'Calculated at checkout'}
                    </span>
                  </div>
                  {subtotal < FREE_SHIPPING_THRESHOLD && subtotal > 0 && (
                    <div className="flex items-center gap-2 text-xs text-[#C6A87C] bg-[#F9F8F6] p-3">
                      <Truck className="h-4 w-4" />
                      <span>Add €{(FREE_SHIPPING_THRESHOLD - subtotal).toFixed(2)} more for free shipping</span>
                    </div>
                  )}
                </div>

                <div className="border-t border-[#EBE5DE] pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="font-heading text-lg text-[#2C2420]">Subtotal</span>
                    <span className="font-heading text-lg text-[#2C2420]" data-testid="total">
                      €{subtotal.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-[#8C8580] mt-1">Shipping calculated at checkout</p>
                </div>

                <Link to="/checkout">
                  <Button className="btn-primary w-full" data-testid="checkout-btn">
                    Proceed to Checkout
                  </Button>
                </Link>

                {/* Trust badges */}
                <div className="mt-6 pt-6 border-t border-[#EBE5DE] space-y-3">
                  <p className="text-xs text-[#8C8580] text-center">
                    Secure checkout powered by Stripe
                  </p>
                  <div className="flex justify-center gap-3">
                    {['Visa', 'Mastercard', 'Amex'].map((card) => (
                      <span
                        key={card}
                        className="px-2 py-1 bg-[#F9F8F6] text-xs text-[#8C8580]"
                      >
                        {card}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};
